import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import LoadingDots from '../../LoadingDots/LoadingDots';
import { collectImageSources } from '../../../utils/imagePreload';
import { useImagePreload } from '../../../hooks/useImagePreload';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const getAlignStyles = (align) => {
  switch ((align || '').toLowerCase()) {
    case 'center':
      return {
        alignItems: 'center',
        textAlign: 'center',
      };
    case 'right':
      return {
        alignItems: 'flex-end',
        textAlign: 'right',
      };
    case 'left':
    default:
      return {
        alignItems: 'flex-start',
        textAlign: 'left',
      };
  }
};

const SeoSnakeMobileComponent = ({ data }) => {
  const cards = Array.isArray(data)
    ? data
    : (data?.cards ?? data?.guides ?? data?.items ?? []);
  const imageSources = useMemo(() => collectImageSources(cards), [cards]);
  const areImagesReady = useImagePreload(imageSources);

  if (!cards.length) return null;

  if (!areImagesReady) {
    return (
      <Box
        sx={{
          width: '100%',
          mt: 4,
          minHeight: '220px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingDots variant="otp" />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {cards.map((card, index) => {
          const alignStyles = getAlignStyles(card.align);

          return (
            <ScrollFadeWrapper key={index}>
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '24px',
                  boxShadow:
                    '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                {/* Image */}
                {card.image && (
                  <Image
                    width={358}
                    height={180}
                    src={card.image}
                    alt={card.imgAltTag || card.title}
                    title={card.imageAltTitle}
                    sx={{
                      width: '100%',
                      height: '180px',
                      objectFit: 'cover',
                      borderRadius: '16px',
                    }}
                  />
                )}

                {/* Content */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    ...alignStyles,
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      lineHeight: '1.5rem',
                      color: '#162F3B',
                      whiteSpace: 'pre-line',
                      '& p': { margin: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: card.title || '' }}
                  ></Typography>

                  <Typography
                    component="div"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '0.95rem',
                      lineHeight: '1.45rem',
                      color: '#162F3B',
                      whiteSpace: 'pre-line',
                      '& p': { m: 0, mb: '1em' },
                      '& p:last-of-type': { mb: 0 },
                      '& strong': { fontWeight: 700 },
                      '& ul': {
                        m: 0,
                        mt: '1em',
                        pl: 0,
                        listStylePosition: 'inside',
                      },
                      '& a': {
                        color: '#C7CB00',
                        textDecoration: 'underline',
                        fontWeight: 700,
                      },
                      '& a:hover': {
                        opacity: 0.85,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: card.body || '' }}
                  ></Typography>

                  {card.redirect && (
                    <Typography
                      component="a"
                      href={card.redirect}
                      sx={{
                        mt: '8px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: '#162F3B',
                        textDecoration: 'none',
                        alignSelf:
                          card.align === 'right'
                            ? 'flex-end'
                            : card.align === 'center'
                              ? 'center'
                              : 'flex-start',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#9EA900',
                        },
                      }}
                    >
                      Learn more
                    </Typography>
                  )}
                </Box>
              </Box>
            </ScrollFadeWrapper>
          );
        })}
      </Box>
    </Box>
  );
};

export default SeoSnakeMobileComponent;
